import React, { useEffect } from 'react';
import { IFeedbackTemplate } from '@bb/domain/dist/types';
import {
  Radio,
  RadioGroup,
  InputTextArea,
  Checkbox,
  Select,
  SelectOption,
  theme,
  Text,
} from 'components';
import styled from 'styled-components';

type ExternalFeedbackQuestionProps = {
  onChange: (index: number, response: string) => void;
  question: IFeedbackTemplate;
  index: number;
};

const ExternalFeedbackQuestion: React.FC<ExternalFeedbackQuestionProps> = ({
  onChange,
  question,
  index,
}) => {
  const [response, setResponse] = React.useState<string>();
  // Need a local state to avoid cursor jump due to side-effect triggered by setResponse in useEffect
  const exisitingTextResponse =
    question.type === 'text' && question.response ? question.response : '';
  const [textResponse, setTextResponse] = React.useState<string>(
    exisitingTextResponse,
  );
  const [checkBoxResponse, setCheckBoxResponse] = React.useState<boolean[]>();

  useEffect(() => {
    if (question.type === 'checkbox') {
      const checkedOptions = question.response?.split('  ') ?? [];
      setCheckBoxResponse(
        question.options.map(item => checkedOptions.includes(item)),
      );
    }
    setResponse(question.response);
  }, [question.response]);

  useEffect(() => {
    if (question.type === 'checkbox' && checkBoxResponse) {
      const newResponse = question.options
        .filter((option, i) => checkBoxResponse[i])
        .join('  ');
      onChange(index, newResponse);
    }
  }, [checkBoxResponse]);

  return (
    <>
      {question.type === 'text' && (
        <div data-cy={`feedback-edit-question-${index}`}>
          <StyledQuestionLabel
            data-cy={`feedback-edit-question-${index}-title`}
          >
            {question.label}
          </StyledQuestionLabel>

          <StyledResizableTextArea
            value={textResponse}
            onChange={e => {
              onChange(index, e.target.value);
              setTextResponse(e.target.value);
            }}
            data-cy={`feedback-edit-question-${index}-textarea`}
            imposeWrap
          />
        </div>
      )}

      {question.type === 'select' && (
        <div data-cy={`feedback-edit-question-${index}`}>
          <StyledQuestionLabel
            data-cy={`feedback-edit-question-${index}-title`}
          >
            {question.label}
          </StyledQuestionLabel>

          <Select
            value={response}
            onChange={value => onChange(index, value)}
            dropdownStyle={{ zIndex: 1051 }} // angular modal has 1050 z-index
          >
            {question.options.map((option, i: number) => (
              <SelectOption
                key={i}
                data-cy={`feedback-edit-question-${index}-option-${i}`}
                value={option}
              >
                {option}
              </SelectOption>
            ))}
          </Select>
        </div>
      )}

      {question.type === 'radio' && (
        <div data-cy={`feedback-edit-question-${index}`}>
          <StyledQuestionLabel
            data-cy={`feedback-edit-question-${index}-title`}
          >
            {question.label}
          </StyledQuestionLabel>

          <RadioGroup
            value={response}
            onChange={e => onChange(index, e.target.value)}
          >
            {question.options.map((option: string, i: number) => (
              <div key={i}>
                <Radio
                  value={option}
                  data-cy={`feedback-edit-question-${index}-option-${i}`}
                >
                  {option}
                </Radio>
              </div>
            ))}
          </RadioGroup>
        </div>
      )}

      {question.type === 'checkbox' && (
        <div data-cy={`feedback-edit-question-${index}`}>
          <StyledQuestionLabel
            data-cy={`feedback-edit-question-${index}-title`}
          >
            {question.label}
          </StyledQuestionLabel>

          {question.options.map((option: string, i: number) => (
            <div key={i}>
              <Checkbox
                data-cy={`feedback-edit-question-${index}-checkbox-${i}`}
                checked={Boolean(checkBoxResponse?.[i])}
                onChange={e => {
                  const newResponse = [...checkBoxResponse];
                  newResponse[i] = e.target.checked;
                  setCheckBoxResponse(newResponse);
                }}
              >
                {option}
              </Checkbox>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default ExternalFeedbackQuestion;

const StyledResizableTextArea = styled(InputTextArea)`
  textarea {
    color: ${theme.textColor};
  }
`;

const StyledQuestionLabel = styled(Text).attrs({ strong: true })`
  display: inline-block;
  margin-bottom: 5px;
`;
